import React, { useState } from 'react';

interface CSVImportGuideProps {
  onClose: () => void;
}

const CSVImportGuide: React.FC<CSVImportGuideProps> = ({ onClose }) => {
  const [copied, setCopied] = useState(false);
  
  // Mock template data until API is fully implemented
  const template = {
    filename: "contacts_template.csv",
    content: "name,phone_number,email,notes\nJohn Doe,+1234567890,john@example.com,Example note\nJane Smith,+9876543210,jane@example.com,Another note"
  };
  const isLoading = false;

  const handleDownloadTemplate = () => {
    if (!template) return;
    
    const blob = new Blob([template.content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = template.filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCopyTemplate = () => {
    if (!template) return;
    
    navigator.clipboard.writeText(template.content)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => console.error('Failed to copy template:', err));
  };

  return (
    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-auto">
      <div className="px-6 py-4 border-b flex justify-between items-center bg-whatsapp-green text-white">
        <h3 className="text-lg font-semibold">CSV Import Guidelines</h3>
        <button 
          onClick={onClose}
          className="text-white hover:text-gray-200"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="px-6 py-4">
        <h4 className="text-lg font-medium mb-3">How to Import Contacts</h4>
        
        <div className="mb-4">
          <p className="mb-2">Follow these steps to import your contacts:</p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Prepare a CSV file with the required columns (see format below)</li>
            <li>Click the "Import CSV" button on the contacts page</li>
            <li>Select your CSV file</li>
            <li>Wait for the import to complete</li>
          </ol>
        </div>

        <div className="mb-4">
          <h5 className="font-medium mb-2">CSV Format Requirements</h5>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Required columns:</strong> name, phone_number</li>
            <li><strong>Optional columns:</strong> email, notes</li>
            <li>Phone numbers must be in international format (e.g., +254712345678)</li>
            <li>The first row must contain column headers</li>
            <li>CSV file should use comma (,) as the delimiter</li>
          </ul>
        </div>

        <div className="mb-4">
          <h5 className="font-medium mb-2">Example CSV Format</h5>
          <div className="bg-gray-50 p-3 rounded-md font-mono text-sm overflow-x-auto">
            name,phone_number,email,notes<br />
            John Doe,+1234567890,john@example.com,Example note<br />
            Jane Smith,+9876543210,jane@example.com,Another note
          </div>
        </div>

        <div className="mb-4">
          <h5 className="font-medium mb-2">CSV Template</h5>
          {isLoading ? (
            <div className="flex justify-center p-4">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-whatsapp-green"></div>
            </div>
          ) : template ? (
            <div className="flex flex-col space-y-2">
              <button
                onClick={handleDownloadTemplate}
                className="bg-whatsapp-green text-white px-4 py-2 rounded-md hover:bg-whatsapp-dark-green"
              >
                Download Template
              </button>
              <button
                onClick={handleCopyTemplate}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
              >
                {copied ? 'Copied!' : 'Copy Template to Clipboard'}
              </button>
            </div>
          ) : (
            <p>Template not available</p>
          )}
        </div>

        <div className="mb-4">
          <h5 className="font-medium mb-2">Tips for Successful Import</h5>
          <ul className="list-disc pl-5 space-y-1">
            <li>Ensure phone numbers are in international format with the plus sign (+)</li>
            <li>Remove any special characters or formatting from phone numbers</li>
            <li>Check for duplicate entries before importing</li>
            <li>For large imports (1000+ contacts), consider splitting into multiple files</li>
            <li>Contacts with phone numbers that already exist in the system will be skipped</li>
          </ul>
        </div>

        <div className="mb-4">
          <h5 className="font-medium mb-2">Common Issues</h5>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Invalid phone format:</strong> Ensure phone numbers include country code with + prefix</li>
            <li><strong>Missing required fields:</strong> Both name and phone_number are required</li>
            <li><strong>Encoding issues:</strong> Save your CSV with UTF-8 encoding</li>
            <li><strong>Duplicate contacts:</strong> Contacts with phone numbers that already exist will be skipped</li>
          </ul>
        </div>
      </div>

      <div className="px-6 py-3 bg-gray-50 flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-whatsapp-green text-white rounded-md hover:bg-whatsapp-dark-green"
        >
          Got it
        </button>
      </div>
    </div>
  );
};

export default CSVImportGuide;
